import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { isOverlayShellOpen } from "../productOverlay/useOverlayShell";

const HEADER_SELECTOR = "header.header-area";
const openShells = [];

const readHeaderBottom = () => {
  const header = document.querySelector(HEADER_SELECTOR);
  return header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
};

const mountContainer = (container) => {
  const main = document.querySelector("main");
  if (main) main.after(container);
  else document.body.prepend(container);
};

export const useOverlayShell = ({ onClose, closeRef, leaving = false }) => {
  const [container] = useState(() => document.createElement("div"));
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const close = useCallback(() => onCloseRef.current(), []);

  useLayoutEffect(() => {
    mountContainer(container);
    let barTop = null;
    const update = () => {
      const next = readHeaderBottom();
      if (next === barTop) return;
      barTop = next;
      container.style.setProperty("--layer-bar-top", `${next}px`);
    };
    update();
    const header = document.querySelector(HEADER_SELECTOR);
    const observer = header && typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    observer?.observe(header);
    window.addEventListener("resize", update);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
      container.remove();
    };
  }, [container]);

  useEffect(() => {
    const previousFocus = document.activeElement;
    openShells.push(container);
    closeRef.current?.focus({ preventScroll: true });

    const handleKeyDown = (event) => {
      if (event.key !== "Escape" || isOverlayShellOpen()) return;
      if (openShells[openShells.length - 1] === container) onCloseRef.current();
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      const index = openShells.indexOf(container);
      if (index >= 0) openShells.splice(index, 1);
      document.removeEventListener("keydown", handleKeyDown);
      if (previousFocus && document.contains(previousFocus)) previousFocus.focus({ preventScroll: true });
    };
  }, [container, closeRef]);

  useEffect(() => {
    if (!leaving) return;
    const index = openShells.indexOf(container);
    if (index >= 0) openShells.splice(index, 1);
  }, [leaving, container]);

  return { container, close };
};
